import React from 'react';
import {StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import {lbColors, lbRadius} from '../leaderboardTheme';
import {LeaderboardRowVM} from '../types';
import PlayerAvatar from './PlayerAvatar';
import RankBadge from './RankBadge';
import TrendPill from './TrendPill';

interface Props {
  entry: LeaderboardRowVM;
  wide: boolean;
  // false for the Top Streaks tab — a point-in-time streak has no
  // meaningful "since yesterday" trend.
  showTrend?: boolean;
}

/**
 * One 64px ranked row: rank badge, avatar, name (+YOU tag), rating subline,
 * win-share progress bar, trend, rating column (wide) and bold wins.
 */
export default function LeaderboardRow({entry, wide, showTrend = true}: Props) {
  const bg = entry.flash
    ? lbColors.flashBg
    : entry.you
    ? lbColors.youRowBg
    : 'transparent';

  return (
    <View style={[styles.row, {backgroundColor: bg}]}>
      <View style={styles.rankCell}>
        <RankBadge rank={entry.rank} medal={entry.medal} />
      </View>
      <PlayerAvatar
        initials={entry.initials}
        color={entry.color}
        avatarUrl={entry.avatarUrl}
        size={40}
      />
      <View style={styles.nameCell}>
        <View style={styles.nameRow}>
          <CCText
            fontSize={15.5}
            fontWeight="bold"
            color={lbColors.ink}
            showEllipses
            style={styles.name}>
            {entry.name}
          </CCText>
          {entry.you ? (
            <View style={styles.youTag}>
              <CCText fontSize={11} fontWeight="bold" color="#FFFFFF">
                YOU
              </CCText>
            </View>
          ) : null}
        </View>
        {!wide ? (
          <CCText
            fontSize={12}
            fontWeight="semibold"
            color={lbColors.muted}
            style={styles.ratingSub}>
            Rating {entry.rating}
          </CCText>
        ) : null}
        <View style={styles.barTrack}>
          <View
            style={[
              styles.barFill,
              {
                width: `${entry.barPct}%`,
                backgroundColor: entry.you
                  ? lbColors.teal
                  : entry.medal
                  ? entry.medal.bg
                  : lbColors.barGold,
              },
            ]}
          />
        </View>
      </View>
      <View style={styles.trendCell}>
        {showTrend ? (
          <TrendPill trend={entry.trend} value={entry.trendAbs} />
        ) : null}
      </View>
      {wide ? (
        <CCText
          fontSize={14}
          fontWeight="semibold"
          color={lbColors.inkSoft}
          style={styles.ratingCol}>
          {entry.rating}
        </CCText>
      ) : null}
      <CCText
        fontSize={21}
        fontWeight="bold"
        color={lbColors.ink}
        style={styles.winsCol}>
        {entry.wins}
      </CCText>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    borderRadius: lbRadius.md,
    height: 64,
  },
  rankCell: {width: 34, alignItems: 'center'},
  nameCell: {flex: 1, minWidth: 0},
  nameRow: {flexDirection: 'row', alignItems: 'center', gap: 8},
  name: {flexShrink: 1},
  youTag: {
    backgroundColor: lbColors.teal,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: lbRadius.pill,
  },
  ratingSub: {marginTop: 2},
  barTrack: {
    height: 4,
    borderRadius: 3,
    backgroundColor: lbColors.barTrack,
    marginTop: 7,
    overflow: 'hidden',
  },
  barFill: {height: '100%', borderRadius: 3},
  trendCell: {width: 46},
  ratingCol: {width: 84, textAlign: 'right'},
  winsCol: {width: 60, textAlign: 'right'},
});
